import { useMemo } from 'react';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useSignIn } from '@clerk/clerk-react';
import { buildTheme } from '../contexts/ThemeContext';
import LandingNav from './landing/LandingNav';
import HeroCopy from './landing/HeroCopy';
import ProductPreview from './landing/ProductPreview';
import PrivacyBand from './landing/PrivacyBand';

// The pre-auth landing renders dark by design, regardless of stored user pref.
// It uses the v3 theme forced to dark (no standalone palette) so every color is
// theme-native; authenticated routes still follow ThemeContext. The shell wraps
// the interactive nav, a two-column hero (copy left, product preview right), and
// the footer privacy band.
const LandingShell = ({ onSignIn, isLoaded }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      {/* Decorative ambient glows. Non-interactive. */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          top: '-15%',
          right: '-10%',
          width: '55%',
          height: '55%',
          background: `radial-gradient(circle, ${alpha(theme.palette.accentDeep, 0.2)}, transparent 70%)`,
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          bottom: '-15%',
          left: '-10%',
          width: '50%',
          height: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.cat.checking, 0.1)}, transparent 70%)`,
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <LandingNav onSignIn={onSignIn} isLoaded={isLoaded} />

        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'grid',
            alignItems: 'center',
            gap: { xs: 5, md: '54px' },
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 520px) 1.18fr' },
            px: { xs: 3, sm: 6 },
            py: { xs: 4, md: 6 },
            maxWidth: 1200,
            mx: 'auto',
            width: '100%',
          }}
        >
          <HeroCopy onSignIn={onSignIn} isLoaded={isLoaded} />
          <ProductPreview />
        </Box>

        <PrivacyBand />
      </Box>
    </Box>
  );
};

const Landing = () => {
  const { signIn, isLoaded } = useSignIn();
  const darkTheme = useMemo(() => buildTheme(true), []);

  const handleGoogleSignIn = async () => {
    if (!isLoaded || !signIn) return;
    await signIn.authenticateWithRedirect({
      strategy: 'oauth_google',
      redirectUrl: '/sso-callback',
      redirectUrlComplete: '/',
    });
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <LandingShell onSignIn={handleGoogleSignIn} isLoaded={isLoaded} />
    </ThemeProvider>
  );
};

export default Landing;
